import React, { useEffect, useRef, useState } from 'react'
import { Check, Moon, Sun } from 'lucide-react'
import {
  THEME_ORDER,
  THEME_SWATCH,
  ThemeId,
  useTheme,
} from '../hooks/use-theme'
import { useTranslation } from '../hooks/use-i18n'

const THEME_LABEL: Record<ThemeId, string> = {
  'green-light': 'Green light',
  'green-dark': 'Green dark',
  'blue-light': 'Blue light',
  'blue-dark': 'Blue dark',
  'red-light': 'Red light',
  'red-dark': 'Red dark',
}

const ThemeToggle: React.FC<{ className?: string; iconSize?: number }> = ({
  className = '',
  iconSize = 18,
}) => {
  const { theme, mode, setTheme } = useTheme()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const openMenu = () => {
    clearCloseTimer()
    setOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => setOpen(false), 160)
  }

  useEffect(() => () => clearCloseTimer(), [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const label = t('nav.toggleTheme')

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2.5 md:p-2 rounded-xl transition-colors text-[color:var(--academ-primary)] hover:bg-[color:var(--academ-primary)]/10 dark:text-[color:var(--academ-dark-accent)] dark:hover:bg-white/10"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
      >
        {mode === 'dark' ? <Sun size={iconSize} /> : <Moon size={iconSize} />}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          className="absolute right-0 top-full z-[80] mt-1.5 w-[11.5rem] overflow-hidden rounded-xl border border-[#0B3333]/12 bg-white py-1.5 shadow-lg dark:border-white/15 dark:bg-[color:var(--academ-dark-card)]"
        >
          <p className="px-3 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-white/50">
            {label}
          </p>
          {THEME_ORDER.map((id) => {
            const swatch = THEME_SWATCH[id]
            const active = theme === id
            const isDark = id.endsWith('dark')
            return (
              <button
                key={id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(id)
                  setOpen(false)
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                  active
                    ? 'bg-[color:var(--academ-page)] font-semibold text-[color:var(--academ-primary)] dark:bg-white/10 dark:text-[color:var(--academ-dark-accent)]'
                    : 'text-[#0B3333] hover:bg-black/[0.04] dark:text-[color:var(--academ-dark-ink)] dark:hover:bg-white/10'
                }`}
              >
                <span
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-black/10 dark:border-white/15"
                  style={{ backgroundColor: swatch.bg, color: swatch.fg }}
                  aria-hidden
                >
                  {isDark ? <Sun size={13} /> : <Moon size={13} />}
                </span>
                <span className="flex-1 leading-tight">{THEME_LABEL[id]}</span>
                <span className="inline-flex w-3.5 shrink-0 justify-end">
                  {active ? <Check size={14} className="opacity-80" /> : null}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ThemeToggle
